import { Agent } from 'models/agent.model';
import { Page } from 'models/page';
import api from '../api/instance';
import { SystemInfo } from '@modules/agent/models/system-info.model';
import { Pageable } from '@models/pageable';
import { CurrentExecutingStepModel } from '@modules/agent/models/current-executing-step.model';
import { TestStepResultPageModel } from '@testsigma/organism';
import { environment } from '@environment';

const nameSpace = '/settings/agents';
const localAgentUrl = environment.driverActionHostAgent;
const agentUrl = '/agent/api/v1/agent';

const findAll = (
  query?: string,
  sort?: string,
  size?: number,
  page?: number,
) => {
  query = query + ',agentType!TRANSIENT';
  return api
    .get(nameSpace, { query, sort, size, page })
    .then((res) => new Page<Agent>().deserialize(res, Agent))
    .catch((e) => Promise.reject(e));
};

const show = (id: number) => {
  return api
    .get(nameSpace + '/' + id)
    .then((res) => new Agent().deserialize(res))
    .catch((e) => Promise.reject(e));
};

const destroy = (id: number): Promise<void> => {
  return api
    .remove(nameSpace + '/' + id)
    .then((res) => console.log(res))
    .catch((err) => new Promise((_, reject) => reject(err)));
};

const find = (id: number): Promise<Agent> => {
  return api
    .get(nameSpace + '/' + id)
    .then((res) => new Agent().deserialize(res))
    .catch((err) => new Promise((_, reject) => reject(err)));
};

const update = (id: number, payload: Agent): Promise<Agent> => {
  return api
    .put(nameSpace + '/' + id, payload.serialize())
    .then((res) => new Agent().deserialize(res))
    .catch((err) => Promise.reject(err));
};

const ping = (): Promise<SystemInfo> => {
  return api
    .get(localAgentUrl + '/agent/api/v1/agent/system_info')
    .then((res) => new SystemInfo().deserialize(res))
    .catch((err) => Promise.reject(err));
};

const stopDebuggerDriver = (tenantId, testCaseResultId): Promise<any> => {
  return api
    .get(
      `${localAgentUrl}/agent/api/v1/device_sessions/stop_web_debugger/${tenantId}/${testCaseResultId}`,
    )
    .then((data) => data)
    .catch((err) => Promise.reject(err));
};

const checkAppConnection = (notify: boolean = false): Promise<any> => {
  return api
    .get(
      localAgentUrl +
        `/agent/api/v1/agent/check_app_connection?notify=${notify}`,
    )
    .then((res) => res)
    .catch((err) => Promise.reject(err));
};

const findByUuid = (agentId: string): Promise<Agent> => {
  return api
    .get(nameSpace + '/' + agentId + '/uuid')
    .then((res) => new Agent().deserialize(res))
    .catch((err) => Promise.reject(err));
};

const amdsAbsent = (uuid?: string) => {
  return api
    .get<boolean>(localAgentUrl + agentUrl + (uuid ? '/' + uuid : '') + '/amds')
    .then((res) => res)
    .catch((err) => Promise.reject(err));
};

const create = (agent: Agent): Promise<Agent> => {
  return api
    .post(nameSpace, agent)
    .then((res) => new Agent().deserialize(res))
    .catch((err) => Promise.reject(err));
};

const registerAgent = (agent: Agent): Promise<void> => {
  return api
    .put(
      localAgentUrl +
        '/agent/api/v1/' +
        agent.uniqueId +
        '/register?jwtApiKey=' +
        agent.jwtApiKey,
      null,
    )
    .then((res) => console.log(res))
    .catch((err) => Promise.reject(err));
};

const findAllPrivateAndPublic = (
  filter?: string,
  sortBy?: string,
  pageable?: Pageable,
): Promise<Page<Agent>> => {
  filter = filter + ',agentType!TRANSIENT';
  return api
    .get(nameSpace + 'all/', { filter })
    .then((res) => new Page<Agent>().deserialize(res, Agent))
    .catch((err) => Promise.reject(err));
};

const getCurrentExecutingStep = (
  sessionId: string,
  tenantId: number,
  testCaseResultId: number,
): Promise<CurrentExecutingStepModel> => {
  return api
    .get(
      `${localAgentUrl}/agent/api/v1/device_sessions/get_current_executing_step/${tenantId}/${sessionId}/${testCaseResultId}`,
    )
    .then((data) => new CurrentExecutingStepModel().deserialize(data))
    .catch((err) => Promise.reject(err));
};

const resumeExecution = (
  sessionId: string,
  tenantId: number,
  testCaseResultId: number,
  stepResultId: string,
  isReRun: boolean,
): Promise<CurrentExecutingStepModel> => {
  return api
    .get(
      `${localAgentUrl}/agent/api/v1/device_sessions/resume_execution/${tenantId}/${sessionId}/${testCaseResultId}/${stepResultId}?isReRun=${isReRun}`,
    )
    .then((data) => new CurrentExecutingStepModel().deserialize(data))
    .catch((err) => Promise.reject(err));
};

const stopExecution = (
  tenantId: number,
  testCaseResultId: number,
): Promise<any> => {
  return api
    .get(
      `${localAgentUrl}/agent/api/v1/device_sessions/stop_execution/${tenantId}/${testCaseResultId}`,
    )
    .then((data) => data)
    .catch((err) => Promise.reject(err));
};

const findAllDebugStepResults = (
  tenantId: number,
  testCaseResultId: number,
) => {
  return new Promise((resolve, reject) => {
    api
      .get(
        `${localAgentUrl}${agentUrl}/step_results/${tenantId}/${testCaseResultId}`,
      )
      .then((res) => resolve(new TestStepResultPageModel().deserialize(res)))
      .catch((error) => reject(error));
  });
};

const reportAgent = (formData): Promise<any> =>
  api.post(`${nameSpace}/reportAgentLogs`, formData, 'multipart/form-data');

export const AgentService = {
  findAll,
  show,
  destroy,
  find,
  update,
  ping,
  checkAppConnection,
  findByUuid,
  amdsAbsent,
  create,
  registerAgent,
  findAllPrivateAndPublic,
  getCurrentExecutingStep,
  resumeExecution,
  findAllDebugStepResults,
  stopExecution,
  reportAgent,
  stopDebuggerDriver,
};
